import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import { qtConnectionManager } from '../lib/qtConnectionmanager'
import { VistCase, VistCaseMetadata } from '../lib/useS3Cases'

interface QtVistCaseManager {
  json: string
  refreshAvailableCases(): void
  downloadCase(jobId: string, signedUrl: string): void
  runCase(jobId: string, sourceUrl: string): void
  checkVersionSelectorCompatibility(caseFilePath: string): void
  getDownloadPath(): string
  startScanning(): void
  stopScanning(): void
  setUserId(userId: string): void
}

interface LocalCase {
  jobId: string
  localPath: string
  lastDownloaded: string
  metadata: VistCaseMetadata
  isValid: boolean
}

interface CombinedCase extends VistCase {
  isLocal?: boolean
  localData?: LocalCase
}

interface VistCaseStore {
  // Server cases (from S3)
  serverCases: VistCase[]
  serverLoading: boolean
  serverError: string | null
  
  // Local cases (from Qt)
  localCases: LocalCase[]
  qtConnected: boolean
  qtError: string | null
  localScanComplete: boolean
  scanning: boolean
  
  // Combined cases (computed)
  combinedCases: CombinedCase[]
  
  downloadProgress: Record<string, number>
  downloadingCases: Set<string>
  runningCases: Set<string>
  
  // Actions
  setServerCases: (cases: VistCase[]) => void
  setServerLoading: (loading: boolean) => void
  setServerError: (error: string | null) => void
  
  setLocalCases: (cases: LocalCase[]) => void
  setQtConnected: (connected: boolean) => void
  setQtError: (error: string | null) => void
  setLocalScanComplete: (complete: boolean) => void
  
  setDownloadProgress: (jobId: string, progress: number) => void
  setDownloadingCases: (cases: Set<string>) => void
  setRunningCases: (cases: Set<string>) => void

  setScanning: (scanning: boolean) => void
  
  addDownloadingCase: (jobId: string) => void
  removeDownloadingCase: (jobId: string) => void
  addRunningCase: (jobId: string) => void
  removeRunningCase: (jobId: string) => void
  
  getCombinedCases: () => CombinedCase[]

  initializeQtConnection: () => Promise<void>
  getQtManager: () => QtVistCaseManager | null
}

let initializationPromise: Promise<void> | null = null

// Create the Zustand store with persistence
export const useVistCaseStore = create<VistCaseStore>()(
  persist(
    (set, get) => ({
      // Initial state
      serverCases: [],
      serverLoading: false,
      serverError: null,
      
      localCases: [],
      qtConnected: false,
      qtError: null,
      localScanComplete: false,
      scanning: false,
      
      combinedCases: [],
      
      downloadProgress: {},
      downloadingCases: new Set(),
      runningCases: new Set(),
      
      // Actions
      setServerCases: (cases) => set({ serverCases: cases }),
      setServerLoading: (loading) => set({ serverLoading: loading }),
      setServerError: (error) => set({ serverError: error }),
      
      setLocalCases: (cases) => set({ localCases: cases }),
      setQtConnected: (connected) => set({ qtConnected: connected }),
      setQtError: (error) => set({ qtError: error }),
      setLocalScanComplete: (complete) => set({ localScanComplete: complete }),
      
      setDownloadProgress: (jobId, progress) => 
        set((state) => ({
          downloadProgress: { ...state.downloadProgress, [jobId]: progress }
        })),
      
      setDownloadingCases: (cases) => set({ downloadingCases: cases }),
      setRunningCases: (cases) => set({ runningCases: cases }),
      
      addDownloadingCase: (jobId: string) => 
        set((state) => ({
          downloadingCases: new Set([...state.downloadingCases, jobId])
        })),
      
      removeDownloadingCase: (jobId: string) => 
        set((state) => {
          const newSet = new Set(state.downloadingCases)
          newSet.delete(jobId)
          return { downloadingCases: newSet }
        }),
      
      addRunningCase: (jobId: string) => 
        set((state) => ({
          runningCases: new Set([...state.runningCases, jobId])
        })),
      
      removeRunningCase: (jobId: string) => 
        set((state) => {
          const newSet = new Set(state.runningCases)
          newSet.delete(jobId)
          return { runningCases: newSet }
        }),
      
      setScanning: (scanning) => set({ scanning: scanning }),
      
      getCombinedCases: () => {
        const { serverCases, localCases } = get()
        
        const allCases = serverCases.map(serverCase => {
          const localCase = localCases.find(lc => lc.jobId === serverCase.jobId)
          const isLocal = !!localCase
          
          return {
            ...serverCase,
            isLocal,
            localData: localCase
          }
        })
        
        // Add any local-only cases that aren't on the server
        const serverJobIds = new Set(serverCases.map(c => c.jobId))
        const localOnlyCases = localCases
          .filter(localCase => !serverJobIds.has(localCase.jobId))
          .map(localCase => ({
            jobId: localCase.jobId,
            filePath: localCase.localPath,
            metadata: {
              author: localCase.metadata.author || 'Unknown',
              version: localCase.metadata.module_version || 'n/a',
              type: localCase.metadata.type || 'vistcase',
              module_id: localCase.metadata.module_id || 'n/a',
              module_version: localCase.metadata.module_version || 'n/a',
              case_id: localCase.metadata.case_id || localCase.jobId,
              case_name: localCase.metadata.case_name || localCase.jobId,
              creation_time: localCase.lastDownloaded,
              platform_version: localCase.metadata.platform_version || 'n/a',
              discipline_level: localCase.metadata.discipline_level || [],
              discipline_tags: localCase.metadata.discipline_tags || [],
              customer_exclusive: localCase.metadata.customer_exclusive || '',
              history: localCase.metadata.history || [],
              description: localCase.metadata.description || 'Local case',
              language: localCase.metadata.language || [''],
              region: localCase.metadata.region || [''],
              guidance: localCase.metadata.guidance || '',
              thumbnail: localCase.metadata.thumbnail || ''
            } as VistCaseMetadata,
            fileSize: 0,
            lastModified: localCase.lastDownloaded,
            isLocal: true,
            localData: localCase
          }))
        
        return [...allCases, ...localOnlyCases]
      },

      initializeQtConnection: async () => {
        try {
          await qtConnectionManager.connect()
        } catch (error) {
          console.log('Qt not available:', error)
          set({ qtError: error as string, qtConnected: false })
        }
      },

      getQtManager: () => {
        const manager = qtConnectionManager.getManager()

        if (manager) return manager

        if (get().qtError) return null

        if (!initializationPromise) {
          console.log('Initializing Qt connection...')
          initializationPromise = qtConnectionManager.connect()
            .then(() => {
              console.log('Qt connection established via lazy initialization')
            })
            .catch(error => {
              console.log('Qt not available:', error)
              initializationPromise = null // Reset so we can try again later if needed
            })
        }

        return qtConnectionManager.getManager()
      }
    }),
    {
      name: 'vist-case-store',
      // Only persist local cases and Qt connection state
      partialize: (state) => ({
        localCases: state.localCases,
        qtConnected: state.qtConnected,
        localScanComplete: state.localScanComplete
      })
    }
  )
)

// Export types for use in components
export type { LocalCase, VistCase, CombinedCase, VistCaseMetadata }
